import { useEffect, useState } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { Loader2, Plus, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { trpc } from '@/lib/trpc';

interface NotificationFormData {
  emailNotifications: boolean;
  newBookingEmail: boolean;
  paymentReceivedEmail: boolean;
  quoteRequestEmail: boolean;
  bookingReminderEmail: boolean;
  reminderDaysBefore: number;
  adminEmails: { email: string }[];
}

interface NotificationsSettingsModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NotificationsSettingsModal({ open, onOpenChange }: NotificationsSettingsModalProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { data: settings, isLoading } = trpc.settings.getNotificationSettings.useQuery(undefined, {
    enabled: open,
  });

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
  } = useForm<NotificationFormData>({
    defaultValues: {
      emailNotifications: true,
      newBookingEmail: true,
      paymentReceivedEmail: true,
      quoteRequestEmail: true,
      bookingReminderEmail: true,
      reminderDaysBefore: 7,
      adminEmails: [{ email: '' }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'adminEmails',
  });

  const emailNotifications = watch('emailNotifications');

  useEffect(() => {
    if (settings) {
      const adminEmailsList = (settings['notifications.adminEmails'] as string[]) || [];
      reset({
        emailNotifications: (settings['notifications.emailNotifications'] as boolean) ?? true,
        newBookingEmail: (settings['notifications.newBookingEmail'] as boolean) ?? true,
        paymentReceivedEmail: (settings['notifications.paymentReceivedEmail'] as boolean) ?? true,
        quoteRequestEmail: (settings['notifications.quoteRequestEmail'] as boolean) ?? true,
        bookingReminderEmail: (settings['notifications.bookingReminderEmail'] as boolean) ?? true,
        reminderDaysBefore: (settings['notifications.reminderDaysBefore'] as number) || 7,
        adminEmails: adminEmailsList.length > 0
          ? adminEmailsList.map((email) => ({ email }))
          : [{ email: '' }],
      });
    }
  }, [settings, reset]);

  const utils = trpc.useUtils();

  const updateMutation = trpc.settings.updateNotificationSettings.useMutation({
    onSuccess: () => {
      utils.settings.getNotificationSettings.invalidate();
      onOpenChange(false);
    },
    onError: () => {
      setIsSubmitting(false);
    },
  });

  const onSubmit = (data: NotificationFormData) => {
    setIsSubmitting(true);
    updateMutation.mutate({
      ...data,
      adminEmails: data.adminEmails.map((e) => e.email).filter((e) => e.trim() !== ''),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Notification Settings</DialogTitle>
          <DialogDescription>
            Configure email and notification preferences.
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {/* Master Switch */}
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <h3 className="font-medium">Email Notifications</h3>
                <p className="text-sm text-muted-foreground">Enable or disable all email notifications</p>
              </div>
              <label className="relative inline-flex cursor-pointer items-center">
                <input
                  type="checkbox"
                  {...register('emailNotifications')}
                  className="peer sr-only"
                />
                <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-primary/20"></div>
              </label>
            </div>

            {/* Notification Types */}
            <div className={`space-y-4 ${!emailNotifications ? 'opacity-50' : ''}`}>
              <h3 className="text-sm font-medium">Notification Types</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">New Booking</p>
                    <p className="text-xs text-muted-foreground">When a new booking is created</p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      {...register('newBookingEmail')}
                      disabled={!emailNotifications}
                      className="peer sr-only"
                    />
                    <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-disabled:cursor-not-allowed"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Payment Received</p>
                    <p className="text-xs text-muted-foreground">When a payment is recorded</p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      {...register('paymentReceivedEmail')}
                      disabled={!emailNotifications}
                      className="peer sr-only"
                    />
                    <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-disabled:cursor-not-allowed"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Quote Request</p>
                    <p className="text-xs text-muted-foreground">When a customer requests a quote</p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      {...register('quoteRequestEmail')}
                      disabled={!emailNotifications}
                      className="peer sr-only"
                    />
                    <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-disabled:cursor-not-allowed"></div>
                  </label>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm">Travel Reminder</p>
                    <p className="text-xs text-muted-foreground">Reminder before travel date</p>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      {...register('bookingReminderEmail')}
                      disabled={!emailNotifications}
                      className="peer sr-only"
                    />
                    <div className="peer h-5 w-9 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-4 after:w-4 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-primary peer-checked:after:translate-x-full peer-checked:after:border-white peer-disabled:cursor-not-allowed"></div>
                  </label>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="reminderDaysBefore">Days Before Travel</Label>
                <Input
                  id="reminderDaysBefore"
                  type="number"
                  min="1"
                  max="30"
                  {...register('reminderDaysBefore', {
                    valueAsNumber: true,
                    min: 1,
                    max: 30,
                  })}
                  disabled={!emailNotifications}
                  className="w-24"
                />
              </div>
            </div>

            {/* Admin Emails */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium">Admin Email Recipients</h3>
                  <p className="text-xs text-muted-foreground">
                    These emails will receive all admin notifications
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ email: '' })}
                >
                  <Plus className="mr-1 h-4 w-4" />
                  Add
                </Button>
              </div>
              <div className="space-y-2">
                {fields.map((field, index) => (
                  <div key={field.id} className="flex gap-2">
                    <Input
                      type="email"
                      {...register(`adminEmails.${index}.email`)}
                      placeholder="admin@company.com"
                    />
                    {fields.length > 1 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => remove(index)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {updateMutation.error && (
              <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">
                {updateMutation.error.message}
              </div>
            )}

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
